import { useCallback, useEffect, useRef, useState } from 'react';
import useDebounce from 'redux/utils/debounceHook';
import type { SaveStatus } from 'toolkit/inputs/SaveStatusIndicator';

const SAVED_VISIBLE_MS = 1750;
// useApiCall flips `isLoading` false in `finally`, then sets the error in
// `catch` on the next microtask — settle briefly so a late error is visible
// before we decide SAVED vs ERROR.
const SETTLE_DELAY_MS = 60;

/**
 * Quill emits `<p><br></p>` for an empty editor. Treat that, blank strings, and
 * markup-only content as empty so we persist `''` instead of sentinel markup.
 * @param value - Editor HTML, plain text, or a missing value.
 * @returns True when there is no user-visible note content.
 */
export const isEmptyNoteHtml = (value: string | null | undefined): boolean => {
  if (!value) return true;
  if (value === '<p><br></p>') return true;
  return value.replace(/<[^>]*>/g, '').trim().length === 0;
};

/**
 * Compares note HTML treating empty-editor variants (`''`, `<p><br></p>`,
 * markup-only) as equal so dirty/autosave checks do not treat a cleared
 * editor as distinct from a saved empty note.
 * @param left - First note value.
 * @param right - Second note value.
 * @returns True when both values are the same text or both are empty.
 */
export const noteHtmlEquals = (
  left: string | null | undefined,
  right: string | null | undefined
): boolean => {
  if (left === right) return true;
  return isEmptyNoteHtml(left) && isEmptyNoteHtml(right);
};

/**
 * Converts editor HTML into the string that should be PATCHed. Empty-editor
 * HTML becomes `''` so an update can clear an existing note without writing
 * Quill sentinels.
 * @param value - Editor HTML or a missing value.
 * @returns Persistable note text.
 */
export const noteHtmlForSave = (
  value: string | null | undefined
): string => (isEmptyNoteHtml(value) ? '' : (value ?? ''));

export interface UseAutosaveOptions {
  /** Current editor value (controlled by the caller). */
  value: string;
  /** Fire-and-forget save trigger — typically a `useApiCall` trigger. */
  onSave: (text: string) => void;
  /** Loading flag of the save request (`useApiCall` loading). */
  isSaving: boolean;
  /** Latest error of the save request (`useApiCall` error response). */
  error?: unknown;
  /** Debounce window before a save fires. */
  debounceMs?: number;
  /** When false, autosave is paused (e.g. read-only or recovery screen open). */
  enabled?: boolean;
}

/** Result of {@link UseAutosaveResult.flushAndWait}. */
export type AutosaveFlushResult = 'saved' | 'error' | 'noop';

export interface UseAutosaveResult {
  status: SaveStatus;
  isDirty: boolean;
  /** Cancel the pending debounce and persist the latest value immediately. */
  flush: () => void;
  /**
   * Like `flush`, but resolves once the in-flight request settles (or
   * immediately when there is nothing to persist).
   */
  flushAndWait: () => Promise<AutosaveFlushResult>;
  /** Mark a value as already persisted (e.g. after an external manual save). */
  markSaved: (value: string) => void;
}

/**
 * Headless autosave engine. Debounces changes, fires `onSave`, and derives a
 * status state machine (IDLE → SAVING → SAVED → IDLE, or ERROR) from the
 * caller's `useApiCall` `isSaving`/`error` signals.
 *
 * Saves are coalesced: while one is in flight new triggers are ignored, and any
 * edits made during the save are persisted once it completes.
 */
export function useAutosave({
  value,
  onSave,
  isSaving,
  error,
  debounceMs = 1500,
  enabled = true,
}: UseAutosaveOptions): UseAutosaveResult {
  const [status, setStatus] = useState<SaveStatus>('IDLE');

  const lastSavedRef = useRef<string>(value);
  const valueRef = useRef<string>(value);
  const enabledRef = useRef<boolean>(enabled);
  const onSaveRef = useRef(onSave);
  const attemptedTextRef = useRef<string>('');
  const errorAtSaveStartRef = useRef<unknown>(error);
  const latestErrorRef = useRef<unknown>(error);
  const prevSavingRef = useRef<boolean>(false);
  const inFlightRef = useRef<boolean>(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushWaitersRef = useRef<Array<(result: AutosaveFlushResult) => void>>(
    []
  );

  const resolveFlushWaiters = useCallback((result: AutosaveFlushResult) => {
    const waiters = flushWaitersRef.current;
    flushWaitersRef.current = [];
    waiters.forEach(resolve => resolve(result));
  }, []);

  // Keep refs current each render without re-subscribing effects.
  valueRef.current = value;
  enabledRef.current = enabled;
  onSaveRef.current = onSave;
  latestErrorRef.current = error;

  const [debouncedValue, cancelDebounce] = useDebounce<string>(
    value,
    debounceMs
  );

  const clearSavedTimer = useCallback(() => {
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
  }, []);

  const triggerSave = useCallback(
    (text: string) => {
      if (!enabledRef.current) return;
      if (inFlightRef.current) return;
      const persistText = noteHtmlForSave(text);
      if (noteHtmlEquals(persistText, lastSavedRef.current)) return;

      inFlightRef.current = true;
      attemptedTextRef.current = persistText;
      errorAtSaveStartRef.current = latestErrorRef.current;
      clearSavedTimer();
      setStatus('SAVING');
      onSaveRef.current(persistText);
    },
    [clearSavedTimer]
  );

  // Autosave when the debounced value settles.
  useEffect(() => {
    triggerSave(debouncedValue);
  }, [debouncedValue, triggerSave]);

  // Resolve the in-flight save when the request finishes (SAVING → SAVED/ERROR).
  useEffect(() => {
    const wasSaving = prevSavingRef.current;
    prevSavingRef.current = isSaving;
    if (!wasSaving || isSaving) return;

    const timer = setTimeout(() => {
      inFlightRef.current = false;
      const failed =
        !!latestErrorRef.current &&
        latestErrorRef.current !== errorAtSaveStartRef.current;

      if (failed) {
        setStatus('ERROR');
        resolveFlushWaiters('error');
        return;
      }

      lastSavedRef.current = attemptedTextRef.current;
      setStatus('SAVED');
      resolveFlushWaiters('saved');
      clearSavedTimer();
      savedTimerRef.current = setTimeout(() => {
        setStatus('IDLE');
        savedTimerRef.current = null;
      }, SAVED_VISIBLE_MS);

      // Persist edits that arrived while the save was in flight, including
      // a cleared editor that should overwrite the previously saved note.
      if (!noteHtmlEquals(valueRef.current, lastSavedRef.current)) {
        triggerSave(valueRef.current);
      }
    }, SETTLE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isSaving, clearSavedTimer, triggerSave, resolveFlushWaiters]);

  // Cleanup the fade timer on unmount.
  useEffect(() => () => clearSavedTimer(), [clearSavedTimer]);

  const flush = useCallback(() => {
    cancelDebounce();
    triggerSave(valueRef.current);
  }, [cancelDebounce, triggerSave]);

  const flushAndWait = useCallback((): Promise<AutosaveFlushResult> => {
    cancelDebounce();

    if (!enabledRef.current) {
      return Promise.resolve('noop');
    }

    const waitForSettled = (): Promise<AutosaveFlushResult> =>
      new Promise(resolve => {
        flushWaitersRef.current.push(resolve);
      });

    if (inFlightRef.current) {
      return waitForSettled();
    }

    const text = valueRef.current;
    if (noteHtmlEquals(text, lastSavedRef.current)) {
      return Promise.resolve('noop');
    }

    const resultPromise = waitForSettled();
    triggerSave(text);
    return resultPromise;
  }, [cancelDebounce, triggerSave]);

  const markSaved = useCallback(
    (saved: string) => {
      lastSavedRef.current = saved;
      inFlightRef.current = false;
      clearSavedTimer();
      setStatus('IDLE');
    },
    [clearSavedTimer]
  );

  const isDirty = !noteHtmlEquals(value, lastSavedRef.current);

  return { status, isDirty, flush, flushAndWait, markSaved };
}

export default useAutosave;
